import * as React from "react";
import { Info } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { type FamilyGroupingMode } from "@/api/files";

export interface ContractFamilyProcessingForm {
  organiseAsContractFamilies: boolean;
  familyGroupingMode: Exclude<FamilyGroupingMode, "none">;
}

interface Props {
  disabled: boolean;
  hasDirectoryPaths: boolean;
}

export const ContractFamilyProcessing: React.FC<Props> = ({ disabled, hasDirectoryPaths }) => {
  const { register, watch } = useFormContext<ContractFamilyProcessingForm>();
  const organiseAsContractFamilies = watch("organiseAsContractFamilies");

  if (!hasDirectoryPaths) return null;

  return (
    <fieldset disabled={disabled} className="space-y-3 border-t border-desert-sand-200 pt-4">
      <label className="flex cursor-pointer items-center justify-between gap-4 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60">
        <span>
          <span className="block text-sm font-semibold text-midnight-blue-500">Organise as contract families</span>
          <span className="block text-xs text-muted-foreground">
            Optional. Analyse related contracts or use the selected folder structure.
          </span>
        </span>
        <span className="relative inline-flex h-6 w-11 shrink-0 items-center">
          <input
            {...register("organiseAsContractFamilies")}
            type="checkbox"
            disabled={disabled}
            className="peer sr-only"
          />
          <span className="absolute inset-0 rounded-full bg-slate-300 transition-colors peer-checked:bg-midnight-blue-500 peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2" />
          <span className="absolute left-1 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
        </span>
      </label>
      {organiseAsContractFamilies && (
        <div className="grid gap-3 md:grid-cols-2">
          <label className="relative flex cursor-pointer flex-col rounded-lg border border-desert-sand-200 bg-white p-3 transition-colors hover:border-coral has-[:disabled]:cursor-not-allowed has-[:checked]:border-coral has-[:disabled]:opacity-60 has-[:checked]:ring-1 has-[:focus-visible]:ring-2 has-[:checked]:ring-coral has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2">
            <input
              {...register("familyGroupingMode")}
              type="radio"
              value="automatic"
              disabled={disabled}
              className="sr-only"
            />
            <span className="text-sm font-semibold text-midnight-blue-500">Detect automatically</span>
            <span className="mt-1 text-xs leading-4 text-muted-foreground">
              Analyse document relationships and group related contracts.
            </span>
          </label>

          <label className="relative flex cursor-pointer flex-col rounded-lg border border-desert-sand-200 bg-white p-3 transition-colors hover:border-coral has-[:disabled]:cursor-not-allowed has-[:checked]:border-coral has-[:disabled]:opacity-60 has-[:checked]:ring-1 has-[:focus-visible]:ring-2 has-[:checked]:ring-coral has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2">
            <input
              {...register("familyGroupingMode")}
              type="radio"
              value="predefined_folders"
              disabled={disabled}
              className="sr-only"
            />
            <span className="text-sm font-semibold text-midnight-blue-500">Use folder structure</span>
            <span className="mt-1 text-xs leading-4 text-muted-foreground">
              Immediate child folders become contract families.
            </span>
          </label>
        </div>
      )}
      <div className="flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-xs text-muted-foreground">
        <Info aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
        The complete folder structure will be preserved in the destination.
      </div>
    </fieldset>
  );
};
